// =============================================================================
// SHANIZ GAMING ADMIN - MAIN APP
// =============================================================================

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AuthProvider } from './context/AuthContext';
import { useAdminRole, roleAllowed } from './hooks/usePermission';
import { AdminRole } from './services/api';
import { ColorModeProvider } from './context/ColorModeContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Games from './pages/Games';
import Tournaments from './pages/Tournaments';
import Transactions from './pages/Transactions';
import Reports from './pages/Reports';
import AntiCheat from './pages/AntiCheat';
import AuditLogs from './pages/AuditLogs';
import Settings from './pages/Settings';
import Leaderboard from './pages/Leaderboard';
import KycReview from './pages/KycReview';
import Payouts from './pages/Payouts';
import Reconciliation from './pages/Reconciliation';
import FraudRisk from './pages/FraudRisk';
import Support from './pages/Support';
import SupportTicketPage from './pages/SupportTicket';
import Roles from './pages/Roles';
import FeatureFlags from './pages/FeatureFlags';
import UserDetail from './pages/UserDetail';
import Analytics from './pages/Analytics';


// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('admin_token');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Role Guard (FR-ADM-01) — client-side gate. SUPER_ADMIN always allowed;
// `roles` undefined = all roles; `roles={[]}` = SUPER_ADMIN only. Denied users
// are bounced back to the dashboard.
const RequireRole: React.FC<{ roles?: AdminRole[]; children: React.ReactNode }> = ({ roles, children }) => {
  const role = useAdminRole();
  if (!roleAllowed(role, roles)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ColorModeProvider>
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="users" element={<Users />} />
                <Route path="users/:id" element={<UserDetail />} />
                <Route path="games" element={<RequireRole roles={['GAME_ADMIN']}><Games /></RequireRole>} />
                <Route path="tournaments" element={<RequireRole roles={['GAME_ADMIN']}><Tournaments /></RequireRole>} />
                <Route path="leaderboard" element={<RequireRole roles={['GAME_ADMIN']}><Leaderboard /></RequireRole>} />
                <Route path="transactions" element={<RequireRole roles={['FINANCE_ADMIN']}><Transactions /></RequireRole>} />
                <Route path="payouts" element={<RequireRole roles={['FINANCE_ADMIN']}><Payouts /></RequireRole>} />
                <Route path="reconciliation" element={<RequireRole roles={['FINANCE_ADMIN']}><Reconciliation /></RequireRole>} />
                <Route path="fraud" element={<RequireRole roles={['RISK_ANALYST']}><FraudRisk /></RequireRole>} />
                <Route path="support" element={<RequireRole roles={['SUPPORT_AGENT']}><Support /></RequireRole>} />
                <Route path="support/:id" element={<RequireRole roles={['SUPPORT_AGENT']}><SupportTicketPage /></RequireRole>} />
                <Route path="kyc" element={<RequireRole roles={['RISK_ANALYST', 'SUPPORT_AGENT']}><KycReview /></RequireRole>} />
                <Route path="reports" element={<RequireRole roles={['RISK_ANALYST', 'SUPPORT_AGENT']}><Reports /></RequireRole>} />
                <Route path="anticheat" element={<RequireRole roles={['GAME_ADMIN', 'RISK_ANALYST']}><AntiCheat /></RequireRole>} />
                <Route path="audit-logs" element={<RequireRole roles={['FINANCE_ADMIN', 'RISK_ANALYST']}><AuditLogs /></RequireRole>} />
                <Route path="analytics" element={<RequireRole roles={['FINANCE_ADMIN', 'GAME_ADMIN']}><Analytics /></RequireRole>} />
                <Route path="settings" element={<RequireRole roles={[]}><Settings /></RequireRole>} />
                <Route path="roles" element={<RequireRole roles={[]}><Roles /></RequireRole>} />
                <Route path="feature-flags" element={<RequireRole roles={['GAME_ADMIN']}><FeatureFlags /></RequireRole>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </AuthProvider>
      </ColorModeProvider>
    </QueryClientProvider>
  );
};

export default App;
